import React, { useState, useEffect } from 'react';
import { RefreshCw, Info } from 'lucide-react';
import { SacredArtpiece, getRandomArtpiece } from '../../constants/sacredArt';
import { SacredArtFullscreenModal } from '../modals/SacredArtFullscreenModal';
import { SacredArtInfoModal } from '../modals/SacredArtInfoModal';

export const SacredArtViewer: React.FC = () => {
  const [currentArtpiece, setCurrentArtpiece] = useState<SacredArtpiece | null>(null);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Initialize with random artwork
  useEffect(() => {
    setCurrentArtpiece(getRandomArtpiece());
  }, []);

  const handleRefresh = () => {
    setImageLoaded(false);
    setCurrentArtpiece(getRandomArtpiece());
  };

  const handleImageClick = () => {
    setIsFullscreenOpen(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  if (!currentArtpiece) {
    return null;
  }

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-sacred-cream dark:bg-sacred-stone-dark p-4 flex items-center justify-between border-b-2 border-sacred-gold-muted">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Sacred Art</h3>
          <button
            onClick={handleRefresh}
            className="p-3 hover:bg-sacred-gold hover:bg-opacity-20 rounded-full transition-colors"
            title="View different artwork"
            aria-label="Show different artwork"
          >
            <RefreshCw className="w-5 h-5 text-sacred-gold-dark dark:text-sacred-gold-light" />
          </button>
        </div>

        {/* Art Display */}
        <div className="relative bg-black aspect-square overflow-hidden cursor-pointer group" onClick={handleImageClick}>
          <img
            src={`/sacred-art/${currentArtpiece.filename}`}
            alt={currentArtpiece.title}
            className={`w-full h-full object-cover transition-all duration-300 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={handleImageLoad}
            loading="lazy"
          />

          {/* Loading State */}
          {!imageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-200 dark:bg-gray-700">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 dark:border-purple-400"></div>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading artwork...</p>
              </div>
            </div>
          )}

          {/* Overlay on Hover */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <p className="text-white text-center text-sm font-medium">Tap to view full screen</p>
            </div>
          </div>

          {/* Info Button Overlay */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsInfoOpen(true);
            }}
            className="absolute top-3 right-3 p-2 bg-white bg-opacity-20 hover:bg-opacity-40 rounded-full transition-all duration-200 backdrop-blur-sm"
            title="View artwork details"
            aria-label="View artwork details"
          >
            <Info className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Art Info */}
        <div className="p-4 space-y-2">
          <h4 className="text-lg font-bold text-gray-900 dark:text-white line-clamp-2">
            {currentArtpiece.title}
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {currentArtpiece.artist}
          </p>
          {currentArtpiece.year && (
            <p className="text-xs text-gray-500 dark:text-gray-500">
              {currentArtpiece.year}
            </p>
          )}
          {currentArtpiece.theme && (
            <p className="inline-block mt-2 px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-xs font-medium rounded">
              {currentArtpiece.theme}
            </p>
          )}
        </div>
      </div>

      {/* Modals */}
      <SacredArtFullscreenModal
        artpiece={currentArtpiece}
        isOpen={isFullscreenOpen}
        onClose={() => setIsFullscreenOpen(false)}
        onShowInfo={() => {
          setIsFullscreenOpen(false);
          setIsInfoOpen(true);
        }}
      />

      <SacredArtInfoModal
        artpiece={currentArtpiece}
        isOpen={isInfoOpen}
        onClose={() => setIsInfoOpen(false)}
      />
    </>
  );
};
